import React from 'react';

import { isNil } from 'es-toolkit';

import { Nullable } from '@clearverify/types';

import {
  EmployerRevenueShareDocument,
  EmployerUpdate,
  useEmployerRevenueShareQuery,
  useEmployerRevenueShareUpdateMutation,
  useRevenueShareSettingDeleteMutationMutation,
} from 'src/api';

import { useNotification } from '../app';

export const useEmployerRevenueShare = (id: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { data, loading } = useEmployerRevenueShareQuery({
    variables: {
      where: { id },
    },
    skip: !id,
  });

  const employerBilling = React.useMemo(() => data?.employer, [data?.employer]);

  const [employerRevenueShareUpdateMutation, { loading: updating }] =
    useEmployerRevenueShareUpdateMutation({
      refetchQueries: [EmployerRevenueShareDocument],
      context: createApolloContext({
        successMessage: 'Employer revenue share has been successfully updated',
      }),
    });

  const [employerBillingDeleteMutation, { loading: deleting }] =
    useRevenueShareSettingDeleteMutationMutation({
      refetchQueries: [EmployerRevenueShareDocument],
      context: createApolloContext({
        successMessage: 'Employer revenue share has been successfully updated',
      }),
    });

  const updateEmployerRevenueShare = async (data: Omit<EmployerUpdate, 'id'>) => {
    if (isNil(id)) {
      return;
    }

    await employerRevenueShareUpdateMutation({
      variables: {
        where: { id },
        data,
      },
    });
  };

  const deleteEmployerBilling = async (data: { id: Nullable<string> }) => {
    if (isNil(data.id)) {
      return;
    }

    await employerBillingDeleteMutation({
      variables: {
        filter: { id: data.id },
      },
    });
  };

  return {
    employerBilling,
    updateEmployerRevenueShare,
    deleteEmployerBilling,
    loading: loading || updating || deleting,
  };
};
